import { useEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { Button, Select, Spinner, Text } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';

import idVerseLogo from './images/id-verse-logo.png';
import { useAddPhoneNumber } from './useAddPhoneNumber';
import { useGetPersonToVerify } from './useGetPersonToVerify';
import { useSendVerificationSMS } from './useSendVerificationSMS';
import { TDeserialisedPerson } from '../../../../modals/idCheckVerificationModal/IdCheckVerificationModal.types';
import { InnerLayout } from '../InnerLayout/InnerLayout';

type TView = 'send' | 'waiting' | 'error' | 'complete';

// Sentinel select value that reveals a free-text input for a number not on file.
const ENTER_DIFFERENT = 'ENTER_DIFFERENT';

// On-file phone numbers with a usable value, deduped by number.
const getPhoneOptions = (person: TDeserialisedPerson): string[] =>
    person.phones
        .map((phone) => phone.value)
        .filter((value): value is string => Boolean(value))
        .filter((value, index, values) => values.indexOf(value) === index);

// Persists a new number, returning the person with that number appended so the
// SMS send can resolve its phone_uid from person.phones.
const withNewPhone = async (
    person: TDeserialisedPerson,
    phoneNumber: string,
    addPhoneNumber: (params: {
        person: TDeserialisedPerson;
        phoneNumber: string;
    }) => Promise<string>
): Promise<TDeserialisedPerson> => {
    const uid = await addPhoneNumber({ person, phoneNumber });
    return {
        ...person,
        phones: [...person.phones, { uid, value: phoneNumber }]
    };
};

export const IdCheck = () => {
    const { idCheckResponse } = useGetPersonToVerify();

    const people = idCheckResponse?.people ?? [];
    const providerName =
        idCheckResponse?.id_verification_provider_name ?? 'IDVerse';
    const showNames = people.length > 1;

    // Local override of the send/waiting/error step. Server status (polled) wins
    // for the terminal complete/waiting states so the view auto-advances.
    const [views, setViews] = useState<Record<string, TView>>({});

    const resolveView = (person: TDeserialisedPerson): TView => {
        if (person.idCheckStatus === 'Verified') return 'complete';
        if (person.idCheckStatus === 'Verifying your check') return 'waiting';
        return views[person.uid] ?? 'send';
    };

    const showIntro = people.some((person) => resolveView(person) === 'send');

    return (
        <InnerLayout
            title={
                showIntro && (
                    <FormattedMessage
                        id="_.funding_requirements.id_check.title"
                        defaultMessage="Identity check"
                    />
                )
            }
            subheading={
                showIntro &&
                (showNames ? (
                    <FormattedMessage
                        id="_.funding_requirements.id_check.subheading_multiple"
                        defaultMessage="We need to verify your identity, as well as the identities of all guarantors, to ensure the funds are sent securely to the correct person. {providerName} is the most secure way to do this."
                        values={{ providerName }}
                    />
                ) : (
                    <FormattedMessage
                        id="_.funding_requirements.id_check.subheading"
                        defaultMessage="We need to check your identity to make sure we’re sending money to the right person. We use {providerName} verification, which is a secure way to do this. You’ll just need your passport or driving license, and your smartphone."
                        values={{ providerName }}
                    />
                ))
            }
        >
            <div className="space-y-xl">
                {showIntro && (
                    <div className="space-y-l">
                        <Text className="text-l">
                            {showNames ? (
                                <FormattedMessage
                                    id="_.funding_requirements.id_check.phone_instruction_multiple"
                                    defaultMessage="Select your mobile number below and click to send yourself a text, which will contain a link for you to complete the check. Do the same for everyone listed so they can complete theirs too (and maybe give them a heads up to speed things along!)."
                                />
                            ) : (
                                <FormattedMessage
                                    id="_.funding_requirements.id_check.phone_instruction"
                                    defaultMessage="Select your mobile number and click to send yourself a text below. Once you receive the text, click on the link to complete your check."
                                />
                            )}
                        </Text>
                    </div>
                )}
                {people.map((person) => (
                    <PersonIdCheck
                        key={person.uid}
                        person={person}
                        showName={showNames}
                        providerName={providerName}
                        view={resolveView(person)}
                        onViewChange={(view) =>
                            setViews((prev) => ({
                                ...prev,
                                [person.uid]: view
                            }))
                        }
                    />
                ))}
            </div>
        </InnerLayout>
    );
};

const PersonIdCheck = ({
    person,
    showName,
    providerName,
    view,
    onViewChange
}: {
    person: TDeserialisedPerson;
    showName: boolean;
    providerName: string;
    view: TView;
    onViewChange: (view: TView) => void;
}) => {
    const { sendVerificationSMS, isLoading: isSending } =
        useSendVerificationSMS();
    const { addPhoneNumber, isLoading: isAddingPhone } = useAddPhoneNumber();

    const phoneOptions = getPhoneOptions(person);

    // Seed the dropdown with the first on-file number; fall back to the
    // "enter a different number" input when the person has none on file.
    const [selectValue, setSelectValue] = useState(
        phoneOptions[0] ?? ENTER_DIFFERENT
    );
    const [customNumber, setCustomNumber] = useState('');

    const isCustom = selectValue === ENTER_DIFFERENT;
    const phoneNumber = isCustom ? customNumber.trim() : selectValue;

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!phoneNumber) return;

        try {
            // A number not on file has no uid; persist it first so the SMS
            // send can resolve its phone_uid, then send against that entry.
            const personToSend = isCustom
                ? await withNewPhone(person, phoneNumber, addPhoneNumber)
                : person;

            await sendVerificationSMS({ person: personToSend, phoneNumber });
            tracking.ampli.fundingRequirementsIdCheckSmsSent({
                version: '2.0'
            });
            onViewChange('waiting');
        } catch {
            // Errors are logged in the hook; show the generic error page.
            onViewChange('error');
        }
    };

    useEffect(() => {
        if (view === 'complete') {
            tracking.ampli.fundingRequirementsIdCheckCompleted({
                version: '2.0'
            });
        }
    }, [view]);

    return (
        <div className="space-y-l">
            {showName && (
                <Text className="fs-exclude text-l font-med text-primary-10">
                    {`${person.firstName} ${person.lastName}`}
                </Text>
            )}
            {view === 'complete' && <IdCheckComplete />}
            {view === 'waiting' && (
                <CheckYourMessages
                    onResend={() => {
                        tracking.ampli.fundingRequirementsIdCheckResendClicked({
                            version: '2.0'
                        });
                        onViewChange('send');
                    }}
                />
            )}
            {view === 'error' && (
                <IdCheckError onRetry={() => onViewChange('send')} />
            )}
            {view === 'send' && (
                <SendText
                    person={person}
                    phoneOptions={phoneOptions}
                    selectValue={selectValue}
                    onSelectChange={setSelectValue}
                    customNumber={customNumber}
                    onCustomNumberChange={setCustomNumber}
                    hasPhoneNumber={Boolean(phoneNumber)}
                    isSending={isSending || isAddingPhone}
                    providerName={providerName}
                    onSubmit={handleSend}
                />
            )}
        </div>
    );
};

const SendText = ({
    person,
    phoneOptions,
    selectValue,
    onSelectChange,
    customNumber,
    onCustomNumberChange,
    hasPhoneNumber,
    isSending,
    providerName,
    onSubmit
}: {
    person: TDeserialisedPerson;
    phoneOptions: string[];
    selectValue: string;
    onSelectChange: (value: string) => void;
    customNumber: string;
    onCustomNumberChange: (value: string) => void;
    hasPhoneNumber: boolean;
    isSending: boolean;
    providerName: string;
    onSubmit: (e: React.FormEvent) => void;
}) => {
    const intl = useIntl();
    const personName = `${person.firstName} ${person.lastName}`;

    return (
        <form onSubmit={onSubmit} className="space-y-l">
            <div className="space-y-s">
                <Select
                    label={
                        <Text className="text-s font-med text-primary-10">
                            <FormattedMessage
                                id="_.funding_requirements.id_check.mobile_number_label"
                                defaultMessage="Mobile number"
                            />
                        </Text>
                    }
                    aria-label={`Mobile number for ${personName}`}
                    className="fs-exclude"
                    wrapperClassName="max-w-[340px]"
                    value={selectValue}
                    onChange={(e) => onSelectChange(e.target.value)}
                >
                    {phoneOptions.map((phone) => (
                        <option key={phone} value={phone}>
                            {phone}
                        </option>
                    ))}
                    <option value={ENTER_DIFFERENT}>
                        {intl.formatMessage({
                            id: '_.funding_requirements.id_check.enter_different_number',
                            defaultMessage: 'Enter a different number'
                        })}
                    </option>
                </Select>
                {selectValue === ENTER_DIFFERENT && (
                    <input
                        type="tel"
                        aria-label={`Different mobile number for ${personName}`}
                        value={customNumber}
                        onChange={(e) => onCustomNumberChange(e.target.value)}
                        placeholder="0800001066"
                        className="fs-exclude w-full max-w-[340px] rounded-m border border-structure-80 px-m py-s"
                    />
                )}
            </div>
            <div className="flex items-center gap-l">
                <Button
                    variant="primary-rounded"
                    type="submit"
                    disabled={!hasPhoneNumber || isSending}
                >
                    {isSending ? (
                        <FormattedMessage
                            id="_.funding_requirements.id_check.sending_text"
                            defaultMessage="Sending..."
                        />
                    ) : (
                        <FormattedMessage
                            id="_.funding_requirements.id_check.send_text"
                            defaultMessage="Send SMS"
                        />
                    )}
                </Button>
                <SecuredByProvider providerName={providerName} />
            </div>
        </form>
    );
};

const SecuredByProvider = ({ providerName }: { providerName: string }) => {
    return (
        <div className="flex items-center gap-s text-s text-structure-40">
            <FormattedMessage
                id="_.funding_requirements.id_check.secured_by"
                defaultMessage="Secured and powered by"
            />
            {/* We only have a logo asset for IDVerse - fall back to the
                provider's name as text rather than showing IDVerse's logo
                for a check that's actually done through a different provider. */}
            {providerName === 'IDVerse' ? (
                <img
                    src={idVerseLogo}
                    alt={providerName}
                    className="h-auto w-[80px]"
                />
            ) : (
                <Text className="text-s font-med text-structure-40">
                    {providerName}
                </Text>
            )}
        </div>
    );
};

const IdCheckComplete = () => {
    const { search } = useLocation();

    return (
        <div>
            <Text className="text-3xl font-bold">
                <FormattedMessage
                    id="_.funding_requirements.id_check.complete_title"
                    defaultMessage="ID check complete"
                />
            </Text>
            <Text className="mt-l text-l">
                <FormattedMessage
                    id="_.funding_requirements.id_check.complete_body"
                    defaultMessage="Everything looks good, you can now continue."
                />
            </Text>

            <div className="mt-xl">
                <Link
                    to={maintainStateKey({
                        pathname: '/funding-requirements/',
                        search
                    })}
                >
                    <Button variant="primary-rounded">
                        <FormattedMessage
                            id="_.funding_requirements.id_check.return_to_list"
                            defaultMessage="Return to list"
                        />
                    </Button>
                </Link>
            </div>
        </div>
    );
};

const CheckYourMessages = ({ onResend }: { onResend: () => void }) => {
    return (
        <div>
            <Text className="text-3xl font-bold">
                <FormattedMessage
                    id="_.funding_requirements.id_check.check_messages_title"
                    defaultMessage="Check your messages"
                />
            </Text>
            <Text className="mt-l text-l">
                <FormattedMessage
                    id="_.funding_requirements.id_check.check_messages_body"
                    defaultMessage="We have texted you a link to complete your ID check. Please complete the ID check."
                />
            </Text>

            <hr className="my-2xl border-secondary-90" />

            <div className="flex flex-col items-center gap-l">
                <Text className="text-m font-med">
                    <FormattedMessage
                        id="_.funding_requirements.id_check.waiting_for_results"
                        defaultMessage="Waiting for ID Check results..."
                    />
                </Text>
                <Spinner data-testid="id-check-loading-spinner" />
                <Text className="text-s text-structure-40">
                    <FormattedMessage
                        id="_.funding_requirements.id_check.waiting_note"
                        defaultMessage="*This may take a few minutes to update"
                    />
                </Text>
            </div>

            <div className="mt-2xl flex justify-center">
                <Button variant="primary-rounded" onClick={onResend}>
                    <FormattedMessage
                        id="_.funding_requirements.id_check.no_text_received"
                        defaultMessage="I didn’t receive a text"
                    />
                </Button>
            </div>
        </div>
    );
};

const IdCheckError = ({ onRetry }: { onRetry: () => void }) => {
    return (
        <div>
            <Text className="text-3xl font-bold">
                <FormattedMessage
                    id="_.funding_requirements.id_check.error_title"
                    defaultMessage="Something went wrong"
                />
            </Text>
            <Text className="mt-l text-l">
                <FormattedMessage
                    id="_.funding_requirements.id_check.error_body"
                    defaultMessage="We couldn’t send your ID check text. Please try again."
                />
            </Text>

            <div className="mt-xl flex">
                <Button variant="primary-rounded" onClick={onRetry}>
                    <FormattedMessage
                        id="_.funding_requirements.id_check.error_retry"
                        defaultMessage="Try again"
                    />
                </Button>
            </div>
        </div>
    );
};
